import React, { useState, useEffect, useRef, useCallback } from 'react'
import Compressor from 'compressorjs'
import Loader from './Loader'
import Icon from './Icon'

const STORAGE_KEY = 'files'
const MAX_FILES = 10
const COMPRESS_OPTIONS = {
  maxWidth: 1280,
  maxHeight: 1280,
  resize: 'contain',
  quality: 0.6,
  convertSize: 5_000_000
}

// Compresse un fichier et renvoie { file, dataUrl }
const compressFile = (file) =>
  new Promise((resolve, reject) => {
    new Compressor(file, {
      ...COMPRESS_OPTIONS,
      mimeType: file.type,
      success(compressed) {
        const reader = new FileReader()
        reader.onload = (e) =>
          resolve({
            file: new File([compressed], file.name, {
              type: compressed.type,
              lastModified: Date.now(),
            }),
            dataUrl: e.target.result,
          })
        reader.onerror = reject
        reader.readAsDataURL(compressed)
      },
      error: reject,
    })
  })

// Calcule les dimensions d'affichage de la popup en respectant le ratio
const computePopupSize = (imgW, imgH) => {
  const maxW = window.innerWidth - 60
  const maxH = window.innerHeight - 120
  const ratio = Math.min(maxW / imgW, maxH / imgH, 1)
  return { width: Math.round(imgW * ratio), height: Math.round(imgH * ratio) }
}

export default function InputFiles({
  id,
  name,
  label,
  disabled,
  files = [],
  onChangeFiles,
  onRemoveFile,
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [bigFile, setBigFile] = useState(null)
  const [previews, setPreviews] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
    } catch {
      return []
    }
  })
  const inputRef = useRef(null)

  // Persiste les previews dans le localStorage à chaque changement
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(previews))
    } catch (err) {
      // Quota dépassé : on log, mais on ne casse pas l'UI
      console.warn('localStorage plein, previews non persistées', err)
    }
  }, [previews])

  const handleAddFiles = useCallback(
    async (event) => {
      const selected = Array.from(event.target.files || [])
      if (selected.length === 0) return

      setIsLoading(true)
      try {
        const compressed = await Promise.all(selected.map(compressFile))

        const newFiles = compressed.map(({ file }) => ({ id: null, src: file }))
        const newPreviews = compressed.map(({ dataUrl }) => dataUrl)

        setPreviews((prev) => [...prev, ...newPreviews])
        onChangeFiles(newFiles)
      } catch (err) {
        console.error('Erreur de compression', err)
      } finally {
        setIsLoading(false)
        event.target.value = ''
      }
    },
    [onChangeFiles]
  )

  const handleRemove = useCallback(
    (index) => {
      setPreviews((prev) => prev.filter((_, i) => i !== index))
      onRemoveFile(index)
    },
    [onRemoveFile]
  )

  const openPopup = useCallback((src) => {
    const img = new Image()
    img.onload = () => {
      const { width, height } = computePopupSize(img.width, img.height)
      setBigFile({ src, width, height })
    }
    img.src = src
  }, [])

  const closePopup = useCallback(() => setBigFile(null), [])

  // Échap pour fermer la popup
  useEffect(() => {
    if (!bigFile) return
    const onKey = (e) => e.key === 'Escape' && closePopup()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [bigFile, closePopup])

  const isMaxReached = files.length >= MAX_FILES

  return (
    <>
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="file"
        multiple
        accept="image/*,text/plain"
        disabled={disabled}
        onChange={handleAddFiles}
      />

      <button
        type="button"
        className="btn"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || isMaxReached}
      >
        {label}
      </button>

      <div
        className={`temp_files hasloader ${isLoading ? 'loading active' : ''} ${
          previews.length ? 'active' : ''
        }`}
      >
        {previews.map((src, index) => {
          const file = files[index]
          return (
            <div key={src.slice(0, 32) + index} className="temp_file">
              <img
                src={src}
                width="100"
                height="100"
                alt=""
                onClick={() => openPopup(src)}
              />
              {(!file || !file.id) && (
                <button
                  type="button"
                  className="del_file"
                  onClick={() => handleRemove(index)}
                >
                  <Icon icon="close" />
                </button>
              )}
            </div>
          )
        })}
        {isLoading && <Loader />}
      </div>

      <div className={`popup_file ${bigFile ? 'show' : ''}`}>
        <div className="popin_file">
          {bigFile && (
            <img
              src={bigFile.src}
              width={bigFile.width}
              height={bigFile.height}
              alt=""
            />
          )}
          <button className="close" onClick={closePopup}>
            <Icon icon="close" />
          </button>
        </div>
        <div className="popout" onClick={closePopup}></div>
      </div>
    </>
  )
}
